// src/components/ui/modal/IconModal.tsx
'use client';

import type { ReactNode, ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type IconModalVariant = 'success' | 'error' | 'warning' | 'info';
export type IconModalSize = 'sm' | 'md' | 'lg';

type IconModalProps = {
  show: boolean;
  onClose: () => void;
  variant?: IconModalVariant;
  size?: IconModalSize;
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  onConfirm?: () => void;
  titleClassName?: string;
  /** 幾秒鐘後自動關閉（可選；不給或 <=0 就不自動關閉） */
  autoCloseSeconds?: number;
};

type VariantPreset = {
  defaultTitle: string;
  iconClass: string;
  Icon: () => ReactElement; // ← 用 ReactElement 取代 JSX.Element
};

const VARIANT_PRESETS: Record<IconModalVariant, VariantPreset> = {
  success: {
    defaultTitle: '操作成功',
    iconClass: 'text-success',
    Icon: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24">
        <g fill="none" stroke="currentColor" strokeWidth={1.5}>
          <circle cx={12} cy={12} r={10} opacity={0.5}></circle>
          <path strokeLinecap="round" strokeLinejoin="round" d="m8.5 12.5l2 2l5-5"></path>
        </g>
      </svg>
    ),
  },
  error: {
    defaultTitle: '系統發生錯誤',
    iconClass: 'text-danger',
    Icon: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24">
        <g fill="none">
          <path
            stroke="currentColor"
            strokeWidth={1.5}
            d="M5.312 10.762C8.23 5.587 9.689 3 12 3s3.77 2.587 6.688 7.762l.364.644c2.425 4.3 3.638 6.45 2.542 8.022S17.786 21 12.364 21h-.728c-5.422 0-8.134 0-9.23-1.572s.117-3.722 2.542-8.022z"
            opacity={0.5}
          ></path>
          <path stroke="currentColor" strokeLinecap="round" strokeWidth={1.5} d="M12 8v5"></path>
          <circle cx={12} cy={16} r={1} fill="currentColor"></circle>
        </g>
      </svg>
    ),
  },
  warning: {
    defaultTitle: '操作失敗',
    iconClass: 'text-warning',
    Icon: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24">
        <g fill="none" stroke="currentColor" strokeWidth={1.5}>
          <circle cx={12} cy={12} r={10} opacity={0.5}></circle>
          <path strokeLinecap="round" d="m14.5 9.5l-5 5m0-5l5 5"></path>
        </g>
      </svg>
    ),
  },
  info: {
    defaultTitle: '提示',
    iconClass: 'text-info',
    Icon: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24">
        <g fill="none">
          <circle
            cx={12}
            cy={12}
            r={10}
            stroke="currentColor"
            strokeWidth={1.5}
            opacity={0.5}
          ></circle>
          <path stroke="currentColor" strokeLinecap="round" strokeWidth={1.5} d="M12 17v-6"></path>
          <circle
            cx={1}
            cy={1}
            r={1}
            fill="currentColor"
            transform="matrix(1 0 0 -1 11 9)"
          ></circle>
        </g>
      </svg>
    ),
  },
};

export default function IconModal({
  show,
  onClose,
  variant = 'info',
  size = 'md',
  title,
  children,
  icon,
  footer,
  closeOnBackdrop = true,
  onConfirm,
  titleClassName,
  autoCloseSeconds,
}: IconModalProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ✨ 新增狀態：追蹤元件是否已掛載 (Hydrated)
  const [mounted, setMounted] = useState(false);

  // ✨ useEffect 確保掛載後設置 mounted = true
  useEffect(() => {
    setMounted(true);
  }, []);

  // ✨ 焦點管理：Modal 顯示時，將焦點設定到確認按鈕或 Modal 內容上
  useEffect(() => {
    if (show) {
      // 使用 requestAnimationFrame 確保 DOM 渲染完成
      requestAnimationFrame(() => {
        // 如果有確認按鈕，焦點給確認按鈕；否則給 Modal 內容本身 (確保鍵盤可操作)
        const elementToFocus = confirmRef.current ?? modalContentRef.current;
        if (elementToFocus) {
          elementToFocus.focus();
        }
      });
    }
  }, [show]);

  // 鎖 body 捲動
  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  // Enter 觸發 onConfirm
  useEffect(() => {
    if (!show || !onConfirm) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        confirmRef.current?.click();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [show, onConfirm]);

  // 自動關閉計時器
  useEffect(() => {
    // 清除舊的 timer
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }

    if (!show) return;
    if (!autoCloseSeconds || autoCloseSeconds <= 0) return;

    autoCloseTimerRef.current = setTimeout(() => {
      onClose();
    }, autoCloseSeconds * 1000);

    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [show, autoCloseSeconds, onClose]);

  const { resolvedIcon, resolvedTitle, iconClass } = useMemo(() => {
    const preset = VARIANT_PRESETS[variant];
    const resolvedTitle = title ?? preset.defaultTitle;
    const resolvedIcon = icon ?? <preset.Icon />;
    return { resolvedIcon, resolvedTitle, iconClass: preset.iconClass };
  }, [variant, title, icon]);

  //if (!show) return null;

  // 檢查是否顯示及是否已掛載
  if (!show || !mounted) return null; // ✨ 只有在 show 且 mounted 時才繼續渲染

  const sizeClass = size === 'sm' ? 'modal-sm' : size === 'lg' ? 'modal-lg' : '';

  return createPortal(
    <>
      <div
        className="modal-backdrop fade show"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        className="modal d-block"
        role="dialog"
        aria-modal="true"
        aria-labelledby="icon-modal-title"
      >
        <div className={`modal-dialog modal-dialog-centered ${sizeClass}`}>
          <div
            className="modal-content shadow-sm rounded-4 border-0 position-relative overflow-hidden"
            ref={modalContentRef}
            tabIndex={-1}
          >
            <button
              type="button"
              className="btn-close position-absolute end-0 top-0 m-3 z-50"
              aria-label="Close"
              onClick={onClose}
            ></button>
            <div className="modal-body text-center p-4">
              <div
                className={`mt-3 mb-5 ${iconClass}`}
                style={{ width: 72, height: 72, margin: '0 auto' }}
              >
                {resolvedIcon}
              </div>
              {resolvedTitle && (
                <h5 id="icon-modal-title" className={`modal-title mb-2 ${titleClassName ?? ''}`}>
                  {resolvedTitle}
                </h5>
              )}
              {children && <div className="text-muted">{children}</div>}
            </div>
            <div className="modal-footer border-0 justify-content-center">
              {footer ?? (
                <>
                  <button type="button" className="btn btn-secondary px-4" onClick={onClose}>
                    關閉
                  </button>
                  {onConfirm && (
                    <button
                      ref={confirmRef}
                      type="button"
                      className="btn btn-red px-4"
                      onClick={onConfirm}
                    >
                      確定
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
